<template>
  <div class="modal" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-xl font-bold">{{ $t('orderDetails') }}</h3>
        <button @click="closeModal" class="ods-close">&times;</button>
      </div>
      <div class="modal-content-container">
        <div class="text-xl font-bold mb-2">
          {{ titleDisplay }} - {{ $t('orderNumber') }} {{ order.orderNumber || order.id.substring(0, 6) }}
        </div>
        <div class="flex items-center justify-between mb-4 text-sm">
          <div class="flex items-center gap-3">
            <div :class="['font-bold', elapsedMinutes >= 30 ? 'text-red-500' : 'text-yellow-500']">
              <i :class="[elapsedMinutes >= 30 ? 'fas fa-exclamation-triangle' : 'fas fa-clock']"></i>
              {{ elapsedTime }}
            </div>
            <div class="ods-muted">{{ formatTime(order.timestamp) }}</div>
          </div>
          <div class="ods-muted">
            <i class="fas fa-user mr-1"></i> {{ getWaiterName(order.waiter) }}
          </div>
        </div>
        <div class="order-items mt-4">
          <div v-for="(item, index) in filteredItems" :key="index">
            <!-- Add yellow separator line before the 6th item (index 5) -->
            <div v-if="index === 5 && filteredItems.length > 5" class="border-t-4 border-yellow-500 my-3"></div>
            
            <div class="order-item p-2" :class="{
                'ods-row-line': index !== 4 || filteredItems.length <= 5,
                'ods-cancelled-row': item.cancelled || item.deleted,
                'bg-blue-600/10': selectedItems[index] && !item.cancelled && !item.deleted,
                'rounded': selectedItems[index]
              }">
              <div class="flex items-start justify-between">
                <div class="flex items-start flex-1">
                  <!-- Item Selection Checkbox -->
                  <div v-if="editMode && !item.cancelled && !item.deleted" class="flex items-center mr-2">
                    <input
                      type="checkbox"
                      :id="'item-' + index"
                      v-model="selectedItems[index]"
                      class="w-5 h-5 rounded" style="accent-color: var(--ods-accent)"
                      @click.stop
                    />
                  </div>

                  <span class="ml-2 text-lg font-bold text-yellow-500">{{ item.quantity || 1 }}x</span>
                  <div class="flex-1">
                    <div class="item-name-wrapper">
                      <div class="item-name-row">
                        <span class="text-lg" :class="{'line-through': item.cancelled || item.deleted}">
                          {{ getItemName(item) }}
                          <span v-if="item.articleNumber" class="ods-muted text-xs ml-1">#{{ item.articleNumber }}</span>
                          <span v-if="item.destination" class="ods-dest text-xs ml-1">{{ item.destination }}</span>
                        </span>
                        <span v-if="item.deleted" class="deleted-badge-compact">GELÖSCHT</span>
                      </div>
                      <div v-if="item.deleted && item.deletedBy" class="deleted-info-compact">
                        Gelöscht von: {{ item.deletedBy }} • {{ item.deletedAt ? formatTime(item.deletedAt) : '' }}
                      </div>
                    </div>
                    <div v-if="item.notes" class="text-yellow-500 text-sm flex items-center justify-between" :class="{'line-through': item.cancelled || item.deleted}">
                      <span>※ {{ showOriginalNote ? item.notes : (item.notesArabic || item.notes) }}</span>
                      <div class="flex gap-2 ml-2">
                        <button
                          v-if="hasLatinChars(item.notes) && language === 'ar' && !item.notesArabic"
                          @click.stop="translateNote(item.notes, index, item)"
                          class="translate-btn"
                          :disabled="isTranslating"
                          title="ترجمة إلى العربية"
                        >
                          <i class="fas fa-language" :class="{ 'fa-spin': isTranslating }"></i>
                        </button>
                        <button
                          v-if="item.notesArabic && hasLatinChars(item.notes) && language === 'ar'"
                          @click.stop="showOriginalNote = !showOriginalNote"
                          class="toggle-note-btn"
                          :title="showOriginalNote ? 'عرض الترجمة' : 'عرض النص الأصلي'"
                        >
                          <i class="fas" :class="showOriginalNote ? 'fa-globe' : 'fa-font'"></i>
                        </button>
                      </div>
                    </div>
                    <div v-if="item.addedAt" class="text-green-400 text-xs">
                      <i class="fas fa-plus-circle mr-1"></i>أضيف {{ formatTime(item.addedAt) }}
                    </div>
                    <div v-if="item.cancelled" class="text-red-400 text-xs">
                      <i class="fas fa-ban mr-1"></i>{{ $t('cancelled') }} {{ item.cancellationTimestamp ? formatTime(item.cancellationTimestamp) : '' }}
                    </div>
                  </div>
                </div>

                <!-- Cancel Item Button - Only show for active items in active orders when not in edit mode -->
                <button
                  v-if="!item.cancelled && !item.deleted && orderStatus !== 'completed' && orderStatus !== 'cancelled' && showCancelButtons && !editMode"
                  @click.stop="confirmCancelItem(index)"
                  class="text-red-500 hover:text-red-700 ml-2"
                >
                  <i class="fas fa-times-circle"></i>
                </button>
              </div>
              
              <!-- Item Cancel Confirmation -->
              <div v-if="currentCancelIndex === index" class="cancel-confirmation p-2 ods-danger-box rounded mt-2">
                <p class="text-xs mb-2">{{ $t('cancelItemConfirmation') }}</p>
                <div class="flex space-x-2">
                  <button
                    @click.stop="cancelItem(index)"
                    class="flex-1 ods-btn !h-8 text-sm" style="background: var(--ods-danger); border-color: var(--ods-danger); color: #fff"
                  >
                    {{ $t('confirm') }}
                  </button>
                  <button
                    @click.stop="currentCancelIndex = null"
                    class="flex-1 ods-btn !h-8 text-sm"
                  >
                    {{ $t('cancel') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="order.notes" class="mt-4 p-2 ods-note-box rounded">
          <div class="text-yellow-500 font-bold">{{ $t('notes') }}:</div>
          <div>{{ order.notes }}</div>
        </div>
      </div>
      <!-- Edit Mode Actions -->
      <div v-if="editMode" class="mt-4 flex flex-col gap-3">
        <!-- Cancel Selected Items Button -->
        <button 
          class="ods-btn ods-btn--danger disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!hasSelectedItems"
          @click.stop="showCancelSelectedConfirmation = true"
        >
          <i class="fas fa-trash-alt mr-2"></i>{{ $t('cancelSelectedItems') }}
        </button>
        
        <!-- Cancel Selected Confirmation -->
        <div v-if="showCancelSelectedConfirmation" class="p-3 ods-danger-box rounded">
          <p class="text-sm mb-2">{{ $t('cancelSelectedConfirmation') }}</p>
          <div class="flex gap-2">
            <button 
              class="flex-1 ods-btn" style="background: var(--ods-danger); border-color: var(--ods-danger); color: #fff"
              @click.stop="cancelSelectedItems"
            >
              {{ $t('confirm') }}
            </button>
            <button 
              class="flex-1 ods-btn"
              @click.stop="showCancelSelectedConfirmation = false"
            >
              {{ $t('cancel') }}
            </button>
          </div>
        </div>
        
        <!-- Exit Edit Mode Button -->
        <button 
          class="ods-btn"
          @click.stop="exitEditMode"
        >
          <i class="fas fa-times mr-2"></i>{{ $t('exitEditMode') }}
        </button>
      </div>
      
      <!-- Edit Mode Toggle Button (only show when not in edit mode) -->
      <div v-if="!editMode" class="mt-4 flex justify-center">
        <button 
          class="ods-btn ods-btn--primary"
          @click.stop="enterEditMode"
        >
          <i class="fas fa-pen mr-2"></i>{{ $t('editOrder') }}
        </button>
      </div>
      
      <div class="mt-4 text-center text-sm ods-muted">
        {{ $t('clickToClose') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'OrderDetailsModal',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentTime: Date.now(),
      timerInterval: null,
      currentCancelIndex: null,
      showCancelButtons: true,
      editMode: false,
      selectedItems: {}, // Object to track selected items by index
      showCancelSelectedConfirmation: false,
      isTranslating: false,
      showOriginalNote: false
    };
  },
  computed: {
    ...mapState(['selectedStation', 'language', 'orders']),
    ...mapGetters(['getMenuItemByArticleNumber']),
    titleDisplay() {
      const isTableOrder = this.order.tableNumber && this.order.tableNumber.toLowerCase() !== 'takeaway';
      return isTableOrder ? `${this.$t('table')} ${this.order.tableNumber}` : this.$t('takeaway');
    },
    filteredItems() {
      let items = [];

      if (!this.selectedStation?.destinationCode) {
        ['K', 'D', 'O'].forEach(section => {
          if (this.order[section]?.items) {
            items = items.concat(this.order[section].items);
          }
        });
      } else {
        const section = this.order[this.selectedStation.destinationCode];
        items = section?.items || [];
      }

      // Don't filter out cancelled or deleted items - we want to show them with strikethrough
      return items;
    },
    elapsedTime() {
      if (!this.order.timestamp) return '';

      const orderTime = typeof this.order.timestamp === 'string'
        ? new Date(this.order.timestamp).getTime()
        : parseInt(this.order.timestamp);

      const elapsed = this.currentTime - orderTime;
      const minutes = Math.floor(elapsed / 60000);
      const hours = Math.floor(minutes / 60);

      if (hours > 0) {
        const remainingMinutes = minutes % 60;
        if (remainingMinutes > 0) {
          return `${hours}h ${remainingMinutes}m`;
        }
        return `${hours}h`;
      }
      return `${minutes}m`;
    },
    elapsedMinutes() {
      if (!this.order.timestamp) return 0;

      const orderTime = typeof this.order.timestamp === 'string'
        ? new Date(this.order.timestamp).getTime()
        : parseInt(this.order.timestamp);

      const elapsed = this.currentTime - orderTime;
      return Math.floor(elapsed / 60000);
    },
    orderStatus() {
      const section = this.order[this.selectedStation?.destinationCode];
      const sectionStatus = section?.status;
      
      if (sectionStatus === 'Completed' || sectionStatus === 'Ready') {
        return 'completed';
      } else if (sectionStatus === 'Preparing' || sectionStatus === 'In Progress') {
        return 'preparing';
      } else if (sectionStatus === 'Cancelled') {
        return 'cancelled';
      }
      return 'new';
    },
    
    hasSelectedItems() {
      // Check if any items are selected
      return Object.values(this.selectedItems).some(value => value === true);
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    getItemName(item) {
      const menuItem = this.getMenuItemByArticleNumber(item.articleNumber);
      
      if (this.language === 'ar') {
        return item.name_ar || (menuItem && menuItem.name_ar) || item.name || item.articleNumber || 'Unknown Item';
      } else {
        return item.name || (menuItem && menuItem.name) || item.name_ar || item.articleNumber || 'Unknown Item';
      }
    },
    getWaiterName(waiterInfo) {
      if (!waiterInfo) return 'Unknown';
      if (typeof waiterInfo === 'object' && waiterInfo.name) return waiterInfo.name;
      if (typeof waiterInfo === 'string') return waiterInfo;
      return 'Unknown';
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = typeof timestamp === 'string' 
        ? new Date(timestamp) 
        : new Date(parseInt(timestamp));
      
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    confirmCancelItem(index) {
      this.currentCancelIndex = index;
    },
    cancelItem(index) {
      // Check if this will be the last uncancelled item
      const remainingUncancelledItems = this.filteredItems.filter((item, i) => 
        i !== index && !item.cancelled
      );
      
      // If this is the last item, cancel this station's portion of the order
      if (remainingUncancelledItems.length === 0) {
        console.log('Last item being cancelled, cancelling this station\'s portion of the order:', this.order.id);
        this.$store.dispatch('cancelOrder', {
          orderId: this.order.id,
          reason: 'All items for this station cancelled by staff'
        }).then(() => {
          console.log('Station portion of order cancelled in database:', this.order.id);
          
          // Force update the local state to ensure UI updates
          const destinationCode = this.selectedStation?.destinationCode;
          if (destinationCode && this.order[destinationCode]) {
            // Force the status to Cancelled
            this.$emit('update-order-status', { destinationCode, status: 'Cancelled' });
          }
          
          // Close the modal immediately when all items for this station are cancelled
          this.$emit('close');
        }).catch(error => {
          console.error('Error cancelling order station:', error);
        });
      } else {
        // Otherwise just cancel this individual item
        this.$store.dispatch('cancelOrderItem', {
          orderId: this.order.id,
          itemIndex: index,
          reason: 'Cancelled from details view'
        }).then(() => {
          console.log('Item cancelled in database');
        }).catch(error => {
          console.error('Error cancelling item:', error);
        });
      }
      
      this.currentCancelIndex = null;
    },
    
    // Edit mode methods
    enterEditMode() {
      this.editMode = true;
      this.selectedItems = {}; // Clear any previous selections
    },
    
    exitEditMode() {
      this.editMode = false;
      this.selectedItems = {};
      this.showCancelSelectedConfirmation = false;
    },
    
    cancelSelectedItems() {
      // Mark selected items as cancelled in the database
      this.showCancelSelectedConfirmation = false;
      
      // Get indices of selected items
      const selectedIndices = Object.entries(this.selectedItems)
        .filter(([, isSelected]) => isSelected)
        .map(([index]) => parseInt(index));
      
      if (selectedIndices.length === 0) {
        return; // Nothing to cancel
      }
      
      // Count remaining uncancelled items after this cancellation
      const alreadyCancelledCount = this.filteredItems.filter(item => item.cancelled).length;
      const willBeCancelledCount = selectedIndices.length;
      const totalItems = this.filteredItems.length;
      
      // If all items will be cancelled (including already cancelled ones)
      const allItemsWillBeCancelled = (alreadyCancelledCount + willBeCancelledCount) === totalItems;
      
      if (allItemsWillBeCancelled) {
        // Cancel this station's portion of the order
        console.log('Cancelling this station\'s portion of the order:', this.order.id);
        this.$store.dispatch('cancelOrder', {
          orderId: this.order.id,
          reason: 'All items for this station cancelled by staff'
        }).then(() => {
          console.log('Station portion of order cancelled in database:', this.order.id);
          
          // Force update the local state to ensure UI updates
          const destinationCode = this.selectedStation?.destinationCode;
          if (destinationCode && this.order[destinationCode]) {
            // Force the status to Cancelled
            this.$emit('update-order-status', { destinationCode, status: 'Cancelled' });
          }
          
          // Close the modal immediately when this station's portion is cancelled
          this.$emit('close');
        }).catch(error => {
          console.error('Error cancelling order station:', error);
        });
      } else {
        // Cancel individual items
        const cancelPromises = selectedIndices.map(index => {
          if (this.filteredItems[index] && !this.filteredItems[index].cancelled) {
            return this.$store.dispatch('cancelOrderItem', {
              orderId: this.order.id,
              itemIndex: index,
              reason: 'Item cancelled by staff'
            });
          }
          return Promise.resolve(); // No action needed for already cancelled items
        });
        
        // Wait for all cancellations to complete
        Promise.all(cancelPromises).then(() => {
          console.log('All selected items cancelled in database');
          
          // Close the modal after a short delay to give visual feedback
          setTimeout(() => {
            this.$emit('close');
          }, 1000);
        }).catch(error => {
          console.error('Error cancelling items:', error);
        });
      }
      
      // Reset selection after cancellation
      this.selectedItems = {};
      this.exitEditMode();
    },
    hasLatinChars(text) {
      if (!text) return false;
      // Check if text contains Latin letters OR any non-Arabic Unicode characters (Hindi, Chinese, etc.)
      const hasLatin = /[a-zA-Z]/.test(text);
      const hasNonArabic = /[^\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF\s\d.,!?;:()-]/u.test(text);
      return hasLatin || hasNonArabic;
    },

    async translateNote(note, itemIndex, item) {
      if (this.isTranslating) {
        return;
      }

      this.isTranslating = true;

      try {
        const restaurantId = this.$store.state.selectedRestaurantId;
        const orderId = this.order.id || this.order.orderId;
        const destinationCode = this.selectedStation?.destinationCode || item.destination;

        console.log('🔍 [MODAL TRANSLATE] ItemIndex:', itemIndex, 'DestinationCode:', destinationCode);

        if (!restaurantId) {
          console.error('❌ [TRANSLATE] No restaurantId in store!');
          return;
        }

        if (!orderId) {
          console.error('❌ [TRANSLATE] No orderId found!');
          return;
        }

        const response = await fetch('https://api.h1-software.com/api/translation/translate-note', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            note: note,
            restaurantId: restaurantId,
            orderId: orderId,
            itemIndex: itemIndex,
            destinationCode: destinationCode
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ [TRANSLATE] Server error:', errorText);
          throw new Error(`Translation failed: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        console.log('✅ [TRANSLATE] Translation successful!', data);

        // Note will auto-update via Firebase listener
      } catch (error) {
        console.error('❌ [TRANSLATE] Error:', error);
      } finally {
        this.isTranslating = false;
      }
    },

    $t(key) {
      const translations = {
        ar: {
          orderDetails: 'تفاصيل الطلب',
          orderNumber: 'رقم الطلب',
          notes: 'ملاحظات',
          clickToClose: 'انقر في أي مكان خارج النافذة للإغلاق',
          cancelItemConfirmation: 'هل أنت متأكد أنك تريد إلغاء هذا العنصر؟',
          confirm: 'تأكيد',
          cancel: 'تراجع',
          cancelled: 'تم الإلغاء',
          editOrder: 'تعديل الطلب',
          exitEditMode: 'إلغاء التعديل',
          cancelSelectedItems: 'إلغاء العناصر المختارة',
          cancelSelectedConfirmation: 'هل أنت متأكد أنك تريد إلغاء جميع العناصر المختارة؟'
        },
        de: {
          orderDetails: 'Bestelldetails',
          orderNumber: 'Bestellnummer',
          notes: 'Notizen',
          clickToClose: 'Klicken Sie außerhalb des Fensters zum Schließen',
          cancelItemConfirmation: 'Sind Sie sicher, dass Sie diesen Artikel stornieren möchten?',
          confirm: 'Bestätigen',
          cancel: 'Abbrechen',
          cancelled: 'Storniert',
          editOrder: 'Bestellung bearbeiten',
          exitEditMode: 'Bearbeitungsmodus verlassen',
          cancelSelectedItems: 'Ausgewählte Artikel stornieren',
          cancelSelectedConfirmation: 'Sind Sie sicher, dass Sie alle ausgewählten Artikel stornieren möchten?'
        }
      };
      return translations[this.language]?.[key] || key;
    }
  },
  mounted() {
    // Update timer every minute
    this.timerInterval = setInterval(() => {
      this.currentTime = Date.now();
    }, 60000); // Update every 60 seconds
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
  position: fixed;
  z-index: 2000;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.8);
  cursor: pointer;
  transition: background-color 0.3s;
}

.modal-content {
  margin: 6% auto;
  padding: 20px;
  width: 80%;
  max-width: 640px;
  border-radius: var(--ods-radius-lg);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  cursor: default;
}

.dark .modal-content {
  background-color: var(--ods-surface);
  color: var(--ods-ink);
  border: 1px solid var(--ods-line);
}

.light .modal-content {
  background-color: var(--ods-surface);
  color: var(--ods-ink);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Deleted items styling */
.item-name-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.deleted-badge-compact {
  background: #dc2626;
  color: var(--ods-ink);
  padding: 2px 6px;
  font-size: 9px;
  font-weight: 600;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.deleted-info-compact {
  font-size: 10px;
  color: #dc2626;
  opacity: 0.7;
  font-style: italic;
  margin-top: 2px;
}

/* Translation buttons */
.translate-btn {
  background: linear-gradient(135deg, #10b981, #059669);
  color: var(--ods-ink);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.translate-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669, #047857);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.translate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.translate-btn i {
  font-size: 12px;
}

.translate-btn .fa-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.toggle-note-btn {
  background: linear-gradient(135deg, var(--ods-accent), var(--ods-accent));
  color: var(--ods-ink);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.toggle-note-btn:hover {
  background: linear-gradient(135deg, var(--ods-accent), #1d4ed8);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.toggle-note-btn i {
  font-size: 12px;
}

.ods-close { color: var(--ods-ink-2); font-size: 28px; line-height: 1; width: 36px; height: 36px; border-radius: var(--ods-radius); background: var(--ods-surface-2); border: 1px solid var(--ods-line); }
.ods-close:hover { color: var(--ods-ink); background: var(--ods-surface-3); }
.ods-row-line { border-bottom: 1px solid var(--ods-line); }
.ods-muted { color: var(--ods-ink-3); }
.ods-dest { background: var(--ods-info-tint); color: var(--ods-info-ink); padding: 1px 5px; border-radius: 2px; font-weight: 700; }
.ods-cancelled-row { background: var(--ods-danger-tint); }
.ods-danger-box { background: var(--ods-danger-tint); border: 1px solid var(--ods-danger); color: var(--ods-ink); }
.ods-note-box { background: var(--ods-prep-tint); border-left: 3px solid var(--ods-prep); color: var(--ods-ink); }
</style>